import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const Dashboard = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingPriority, setUpdatingPriority] = useState(false);
  const [updatingCategoryPriority, setUpdatingCategoryPriority] = useState(false);

  // State for the news code search
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  // Inline style objects for the search UI
  const searchBarStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    marginBottom: "1rem",
  };

  const searchInputStyle = {
    flex: 1,
    padding: "0.5rem 0.75rem",
    fontSize: "1rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
  };

  const searchButtonStyle = {
    backgroundColor: "#007bff",
    color: "#ffffff",
    fontSize: "1rem",
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  };

  // Style to make the "فعالیت" (actions) column wider
  const actionColumnStyle = {
    width: "200px",
    minWidth: "150px",
    textAlign: "center",
  };

  const fetchArticles = async () => {
    const accessToken = localStorage.getItem("access");

    if (!accessToken) {
      navigate("/admin/login");
      return;
    }

    setLoading(true);

    try {
      // Build query params conditionally
      const params = {};
      if (searchQuery) {
        params.news_code = searchQuery; // Attach news_code if searchQuery isn't empty
      }

      // Fetch data from the API
      const response = await axios.get(
        `https://khanehyeghtesad.com:8000/api/article/list/${currentPage}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params,
        }
      );

      console.log("API Response:", response.data);

      const articlesData = response.data.articles || [];
      const totalPagesData = response.data.total_pages || 1;

      setArticles(articlesData);
      setTotalPages(totalPagesData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching articles:", err.response?.data || err.message);

      // If unauthorized (401) or forbidden (403), clear token & redirect to login
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("access");
        // localStorage.removeItem("refresh"); // if you have a refresh token as well

        navigate("/admin/login");
        return;
      }

      setError(err.response?.data?.message || "Error fetching articles.");
      setLoading(false);
    }
  };

  useEffect(() => {
    // Re-fetch articles when currentPage or searchQuery changes
    fetchArticles();
    // eslint-disable-next-line
  }, [currentPage, searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = async (articleId) => {
    const accessToken = localStorage.getItem("access");

    if (!accessToken) {
      navigate("/admin/login");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this article?")) {
      return;
    }

    try {
      const response = await axios.delete(
        "https://khanehyeghtesad.com:8000/api/article/delete/",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          data: { article_id: articleId },
        }
      );

      console.log("Delete Response:", response.data);
      alert(response.data.message || "Article deleted successfully.");

      setArticles((prevArticles) =>
        prevArticles.filter((article) => article._id !== articleId)
      );
    } catch (err) {
      console.error("Error deleting article:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error deleting article.");
    }
  };

  const getAssignedPriorities = (excludeArticleId = null) => {
    const priorities = {};
    articles.forEach((article) => {
      if (article.priority && article._id !== excludeArticleId) {
        priorities[article.priority] = article._id;
      }
    });
    return priorities;
  };

  const handlePriorityChange = async (articleId, newPriority) => {
    const accessToken = localStorage.getItem("access");

    if (!accessToken) {
      navigate("/admin/login");
      return;
    }

    setUpdatingPriority(true);

    if (newPriority === "") {
      // Remove priority
      try {
        const response = await axios.post(
          "https://khanehyeghtesad.com:8000/api/article/maketitr/",
          {
            titr_number: null,
            article_id: articleId,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Priority Removed:", response.data);
        alert(response.data.message || "Priority removed successfully.");

        setArticles((prevArticles) =>
          prevArticles.map((article) =>
            article._id === articleId
              ? { ...article, priority: null }
              : article
          )
        );
      } catch (err) {
        console.error("Error removing priority:", err.response?.data || err.message);
        alert(err.response?.data?.message || "Error removing priority.");
      } finally {
        setUpdatingPriority(false);
      }
      return;
    }

    // Assign new priority
    const titr_number = parseInt(newPriority, 10);
    const assignedPriorities = getAssignedPriorities(articleId);
    if (assignedPriorities[titr_number]) {
      alert(`Priority ${titr_number} is already assigned to another article.`);
      setUpdatingPriority(false);
      return;
    }

    try {
      const response = await axios.post(
        "https://khanehyeghtesad.com:8000/api/article/maketitr/",
        {
          titr_number: titr_number,
          article_id: articleId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Priority Assigned:", response.data);
      alert(response.data.message || "Priority assigned successfully.");

      setArticles((prevArticles) =>
        prevArticles.map((article) =>
          article._id === articleId
            ? { ...article, priority: titr_number }
            : article
        )
      );
    } catch (err) {
      console.error("Error assigning priority:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error assigning priority.");
    } finally {
      setUpdatingPriority(false);
    }
  };

  const handleCategoryPriorityChange = async (articleId, category, newPriority) => {
    const accessToken = localStorage.getItem("access");

    if (!accessToken) {
      navigate("/admin/login");
      return;
    }

    setUpdatingCategoryPriority(true);

    if (newPriority === "") {
      // Remove category priority
      try {
        const response = await axios.post(
          "https://khanehyeghtesad.com:8000/api/category/maketitr/",
          {
            titr_number: null,
            article_id: articleId,
            category: category,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Category Priority Removed:", response.data);
        alert(response.data.message || "Category priority removed successfully.");

        setArticles((prevArticles) =>
          prevArticles.map((article) =>
            article._id === articleId
              ? { ...article, category_priority: null }
              : article
          )
        );
      } catch (err) {
        console.error("Error removing category priority:", err.response?.data || err.message);
        alert(err.response?.data?.message || "Error removing category priority.");
      } finally {
        setUpdatingCategoryPriority(false);
      }
      return;
    }

    // Assign new category priority
    const titr_number = parseInt(newPriority, 10);
    try {
      const response = await axios.post(
        "https://khanehyeghtesad.com:8000/api/category/maketitr/",
        {
          titr_number: titr_number,
          article_id: articleId,
          category: category,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Category Priority Assigned:", response.data);
      alert(response.data.message || "Category priority assigned successfully.");

      setArticles((prevArticles) =>
        prevArticles.map((article) =>
          article._id === articleId
            ? { ...article, category_priority: titr_number }
            : article
        )
      );
    } catch (err) {
      console.error("Error assigning category priority:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error assigning category priority.");
    } finally {
      setUpdatingCategoryPriority(false);
    }
  };

  // -------------------------------------------------------------------
  // PAGINATION WITH ELLIPSIS
  // -------------------------------------------------------------------
  const getPaginationPages = () => {
    const pages = [];
    // If totalPages <= 10, just show all pages
    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Always show the first page
    pages.push(1);

    // Calculate start/end around the current page
    const start = Math.max(currentPage - 2, 2);
    const end = Math.min(currentPage + 2, totalPages - 1);

    // If there's a gap between 1 and start, add '...'
    if (start > 2) {
      pages.push("...");
    }

    // Add pages from start to end
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // If there's a gap between end and totalPages-1, add '...'
    if (end < totalPages - 1) {
      pages.push("...");
    }

    // Always show the last page
    pages.push(totalPages);

    return pages;
  };

  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------

  if (loading) {
    return <div>Loading articles...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  if (!articles || articles.length === 0) {
    return <div>No articles found.</div>;
  }

  const allAssignedPriorities = getAssignedPriorities();

  return (
    <div className="dashboard">
      {/* --- SEARCH BAR --- */}
      <div style={searchBarStyle}>
        <input
          type="text"
          placeholder="جستجو بر اساس کد خبر"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={searchInputStyle}
        />
        <button
          onClick={() => {
            // Go back to the first page with the current query
            setCurrentPage(1);
          }}
          style={searchButtonStyle}
        >
          جستجو
        </button>
        {searchQuery && (
          <button
            onClick={() => {
              // Reset the search
              setSearchQuery("");
              setCurrentPage(1);
            }}
            style={{
              ...searchButtonStyle,
              backgroundColor: "#dc3545", // Red color for Close button
            }}
          >
            بستن
          </button>
        )}
      </div>

      {/* --- ARTICLES TABLE --- */}
      <table className="article-table" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th>عنوان</th>
            <th>لید</th>
            <th>متن مقاله</th>
            <th>تاریخ ایجاد</th>
            <th>گروه</th>
            <th>کلیدواژه‌ها</th>
            <th>تعداد بازدید</th>
            <th>اولویت</th>
            <th>اولویت گروه</th>
            {/* Apply our actionColumnStyle to the "فعالیت" header */}
            <th style={actionColumnStyle}>فعالیت</th>
          </tr>
        </thead>
        <tbody>
          {articles.map((article) => (
            <tr key={article._id} style={{ borderBottom: "1px solid #ccc" }}>
              <td>{article.title.slice(0, 15)}</td>
              <td>
                {article.caption.length > 30
                  ? `${article.caption.slice(0, 30)}...`
                  : article.caption}
              </td>
              <td>{article.article.slice(0, 50)}...</td>
              <td>
                {article.jalali_full_date.slice(0, 4)} ({article.iran_time})
              </td>
              <td>
                {Array.isArray(article.category)
                  ? article.category.join(", ")
                  : article.category || "No Category"}
              </td>
              <td>
                {Array.isArray(article.tags)
                  ? article.tags.join(", ")
                  : article.tags || "No Tags"}
              </td>
              <td>{article.view_count}</td>
              <td>
                <select
                  value={article.priority || ""}
                  onChange={(e) => handlePriorityChange(article._id, e.target.value)}
                  disabled={updatingPriority}
                >
                  <option value="">None</option>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option
                      key={num}
                      value={num}
                      disabled={
                        allAssignedPriorities[num] &&
                        allAssignedPriorities[num] !== article._id
                      }
                    >
                      {num}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={article.category_priority || ""}
                  onChange={(e) =>
                    handleCategoryPriorityChange(article._id, article.category, e.target.value)
                  }
                  disabled={updatingCategoryPriority}
                >
                  <option value="">None</option>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </td>
              {/* Apply our actionColumnStyle to the action cell */}
              <td style={actionColumnStyle}>
                <button onClick={() => navigate(`/admin/posts/${article._id}/edit`)}>
                  Edit
                </button>
                <button onClick={() => handleDelete(article._id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* --- PAGINATION --- */}
      <div className="pagination" style={{ marginTop: "1rem" }}>
        {getPaginationPages().map((page, index) => {
          if (page === "...") {
            // Render ellipsis as a non-clickable span
            return (
              <span key={`ellipsis-${index}`} style={{ margin: "0 4px" }}>
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              disabled={currentPage === page}
              style={{
                margin: "0 4px",
                padding: "0.4rem 0.8rem",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                backgroundColor: currentPage === page ? "#ccc" : "#b6",
              }}
            >
              {page}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
